import { TranscriptionConfig } from './types'
import { Checkbox, NumberSlider, Select } from './FormControls'

interface DrumsTabProps {
  config: TranscriptionConfig
  onChange: (config: TranscriptionConfig) => void
}

export function DrumsTab({ config, onChange }: DrumsTabProps) {
  const update = (key: keyof TranscriptionConfig, value: unknown) => {
    onChange({ ...config, [key]: value })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Checkbox
          label="LarsNet Drums"
          value={config.larsnet}
          onChange={(v) => update('larsnet', v)}
          tooltip="Use LarsNet for advanced drum transcription (recommended)"
        />

        <Checkbox
          label="Velocity Dynamics"
          value={config.velocity}
          onChange={(v) => update('velocity', v)}
          tooltip="Include velocity dynamics in MIDI output"
        />

        <Checkbox
          label="Sound Match"
          value={config.soundMatch}
          onChange={(v) => update('soundMatch', v)}
          tooltip="Match detected drums to specific sound samples"
        />

        <Checkbox
          label="Enhanced Analysis"
          value={config.enhanced}
          onChange={(v) => update('enhanced', v)}
          tooltip="Enable enhanced drum analysis (slower but more accurate)"
        />

        <Checkbox
          label="Explain Selection"
          value={config.explain}
          onChange={(v) => update('explain', v)}
          tooltip="Log detailed explanations for sound selection decisions"
        />
      </div>

      <div className="space-y-4 mt-4 p-3 bg-surface-900/50 border border-white/5 rounded-lg">
        <h4 className="text-xs font-medium text-gray-400 uppercase">Onset Detection Thresholds</h4>

        <NumberSlider
          label="Global Onset Filter"
          value={config.onsetFilter}
          onChange={(v) => update('onsetFilter', v)}
          min={0.1}
          max={1.0}
          step={0.05}
          tooltip="Global threshold for onset detection (0.1 = sensitive, 1.0 = strict)"
        />

        <NumberSlider
          label="Kick Onset"
          value={config.onsetKick}
          onChange={(v) => update('onsetKick', v)}
          min={0.1}
          max={1.0}
          step={0.05}
          allowNull
          nullLabel="Use Global"
          tooltip="Kick drum onset threshold (overrides global if set)"
        />

        <NumberSlider
          label="Snare Onset"
          value={config.onsetSnare}
          onChange={(v) => update('onsetSnare', v)}
          min={0.1}
          max={1.0}
          step={0.05}
          allowNull
          nullLabel="Use Global"
          tooltip="Snare drum onset threshold (overrides global if set)"
        />

        <NumberSlider
          label="Hi-hat Onset"
          value={config.onsetHihat}
          onChange={(v) => update('onsetHihat', v)}
          min={0.1}
          max={1.0}
          step={0.05}
          allowNull
          nullLabel="Use Global"
          tooltip="Hi-hat onset threshold (overrides global if set)"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Log Level"
          value={config.logLevel}
          options={[
            { value: 0, label: 'Silent' },
            { value: 1, label: 'Normal' },
            { value: 2, label: 'Verbose' },
            { value: 3, label: 'Debug' },
          ]}
          onChange={(v) => update('logLevel', Number(v))}
          tooltip="Verbosity level for transcription logs"
        />
      </div>
    </div>
  )
}
